import {
  AssignedModulesResponse,
  PerformanceReportingMetricsResponse,
  PerformanceReportingTrendsResponse,
  PerformanceReportingDimensionsResponse,
  PerformanceReportingTop5SummariesResponse,
  TrendValue,
} from '../_types';
import { ASSIGNED_MODULES, getSubjectIdentifier, formatTooltipDescription } from '../_constants';
import { UseOfForceGenerator } from './generators/useOfForceGenerator';
import { VehiclePursuitsGenerator } from './generators/vehiclePursuitsGenerator';
import { InternalAffairsGenerator } from './generators/internalAffairsGenerator';

// Helper function to cache/retrieve data from sessionStorage
function getCachedOrGenerate<T>(key: string, generator: () => T): T {
  try {
    const cached = sessionStorage.getItem(key);
    if (cached) {
      return JSON.parse(cached) as T;
    }
    const data = generator();
    sessionStorage.setItem(key, JSON.stringify(data));
    return data;
  } catch (error) {
    // If sessionStorage fails, just generate fresh data
    return generator();
  }
}

// Helper to generate cache key with assignedModule
function getCacheKey(
  orgId: string,
  assignedModule: string,
  startDate: string,
  endDate: string,
  endpoint: string
): string {
  return `risk_pool_perf_report_${orgId}_${assignedModule}_${startDate}_${endDate}_${endpoint}`;
}

// Helper to generate random number in range
function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

// Helper to generate random float in range
function randomFloat(min: number, max: number, decimals: number = 1): number {
  const value = Math.random() * (max - min) + min;
  return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
}

// Helper to calculate number of periods based on date range
function calculatePeriods(startDate: string, endDate: string): { useWeeks: boolean; periods: number; } {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffDays = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

  // If period is 31 days or less, use weeks
  const useWeeks = diffDays <= 31;

  if (useWeeks) {
    // Calculate number of weeks
    const weeks = Math.ceil(diffDays / 7);
    return { useWeeks: true, periods: Math.max(1, weeks) };
  }

  // For more than a month, calculate number of complete months
  const monthsDiff =
    (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1; // +1 to include both start and end months

  return { useWeeks: false, periods: Math.max(1, monthsDiff) };
}

// Generate assigned modules
export function generateAssignedModules(orgId: string): AssignedModulesResponse {
  const cacheKey = getCacheKey(orgId, 'all', '', '', 'assigned_modules');

  return getCachedOrGenerate(cacheKey, () => {
    return [...ASSIGNED_MODULES];
  });
}

// Generate metrics based on assigned module
export function generateMetrics(
  orgId: string,
  assignedModule: string,
  startDate: string,
  endDate: string
): PerformanceReportingMetricsResponse {
  const cacheKey = getCacheKey(orgId, assignedModule, startDate, endDate, 'metrics');

  return getCachedOrGenerate(cacheKey, () => {
    const metrics: PerformanceReportingMetricsResponse = [];

    switch (assignedModule) {
      case 'internal-affairs': {
        return InternalAffairsGenerator.generateMetrics(orgId, startDate, endDate);
      }
      case 'use-of-force': {
        return UseOfForceGenerator.generateMetrics(orgId, startDate, endDate);
      }
      case 'vehicle-pursuits': {
        return VehiclePursuitsGenerator.generateMetrics(orgId, startDate, endDate);
      }
      case 'field-investigation': {
        const totalInvestigations = randomInt(100, 500);
        const withSearch = randomInt(Math.floor(totalInvestigations * 0.2), Math.floor(totalInvestigations * 0.6));
        const percentWithSearch = totalInvestigations > 0 ? Math.round((withSearch / totalInvestigations) * 1000) / 1000 : 0;
        const withAction = randomInt(Math.floor(totalInvestigations * 0.3), Math.floor(totalInvestigations * 0.7));
        const percentWithAction = totalInvestigations > 0 ? Math.round((withAction / totalInvestigations) * 1000) / 1000 : 0;

        metrics.push(
          {
            key: 'totalInvestigations',
            label: 'Total Field Investigations',
            value: totalInvestigations,
            valueFormat: 'number',
            trend: randomInt(0, 1) === 0 ? 'DOWN' : 'UP',
            description: 'Total number of field investigations during the selected date range',
          },
          {
            key: 'percentWithSearch',
            label: '% Resulting in Search',
            value: percentWithSearch,
            valueFormat: 'percent',
            trend: null,
            description: 'Percentage that resulted in a search (Reason for search not null)',
          },
          {
            key: 'percentWithAction',
            label: '% With Action Taken',
            value: percentWithAction,
            valueFormat: 'percent',
            trend: null,
            description: 'Percentage with any action taken (Action taken is not null)',
          }
        );
        break;
      }
      case 'employee-conduct': {
        const totalComplaints = randomInt(40, 150);
        const sustainedComplaints = randomInt(Math.floor(totalComplaints * 0.2), Math.floor(totalComplaints * 0.5));
        const percentSustained = totalComplaints > 0 ? Math.round((sustainedComplaints / totalComplaints) * 1000) / 1000 : 0;
        const totalCompliments = randomInt(20, 80);

        metrics.push(
          {
            key: 'totalComplaints',
            label: 'Total Complaints',
            value: totalComplaints,
            valueFormat: 'number',
            trend: randomInt(0, 1) === 0 ? 'DOWN' : 'UP',
            description: 'Total complaints (report type = Complaint)',
          },
          {
            key: 'percentSustained',
            label: '% Sustained',
            value: percentSustained,
            valueFormat: 'percent',
            trend: null,
            description: 'Percentage of complaints with disposition = Sustained',
          },
          {
            key: 'totalCompliments',
            label: 'Total Compliments',
            value: totalCompliments,
            valueFormat: 'number',
            trend: null,
            description: 'Total compliments (Report type = Compliment)',
          }
        );
        break;
      }
      case 'vehicle-damage': {
        const totalIncidents = randomInt(50, 200);
        const officerVehicleDamage = randomInt(Math.floor(totalIncidents * 0.3), Math.floor(totalIncidents * 0.7));
        const percentOfficerVehicleDamage = totalIncidents > 0 ? Math.round((officerVehicleDamage / totalIncidents) * 1000) / 1000 : 0;
        const suspectVehicleDamage = randomInt(Math.floor(totalIncidents * 0.4), Math.floor(totalIncidents * 0.8));
        const percentSuspectVehicleDamage = totalIncidents > 0 ? Math.round((suspectVehicleDamage / totalIncidents) * 1000) / 1000 : 0;
        const withInjury = randomInt(0, Math.floor(totalIncidents * 0.2));
        const percentWithInjury = totalIncidents > 0 ? Math.round((withInjury / totalIncidents) * 1000) / 1000 : 0;

        metrics.push(
          {
            key: 'totalIncidents',
            label: 'Total Incidents',
            value: totalIncidents,
            valueFormat: 'number',
            trend: randomInt(0, 1) === 0 ? 'DOWN' : 'UP',
            description: 'Total vehicle-damage incidents during the selected time range',
          },
          {
            key: 'percentOfficerVehicleDamage',
            label: '% Officer Vehicle Damage',
            value: percentOfficerVehicleDamage,
            valueFormat: 'percent',
            trend: null,
            description: 'Percentage of incidents with officer vehicle damage',
          },
          {
            key: 'percentSuspectVehicleDamage',
            label: '% Suspect Vehicle Damage',
            value: percentSuspectVehicleDamage,
            valueFormat: 'percent',
            trend: null,
            description: 'Percentage of incidents with suspect vehicle damage',
          },
          {
            key: 'percentWithInjury',
            label: '% With Any Injury',
            value: percentWithInjury,
            valueFormat: 'percent',
            trend: null,
            description: 'Percentage of incidents with any injury',
          }
        );
        break;
      }
    }

    return metrics;
  });
}

// Generate trends data
export function generateTrends(
  orgId: string,
  assignedModule: string,
  startDate: string,
  endDate: string
): PerformanceReportingTrendsResponse {
  const cacheKey = getCacheKey(orgId, assignedModule, startDate, endDate, 'trends');

  return getCachedOrGenerate(cacheKey, () => {
    const start = new Date(startDate);
    const { useWeeks, periods } = calculatePeriods(startDate, endDate);
    const periodMode = useWeeks ? 'week' : 'month';

    // Handle use-of-force, vehicle-pursuits, and internal-affairs specially - use their generators
    if (assignedModule === 'use-of-force') {
      return UseOfForceGenerator.generateTrends(orgId, startDate, endDate);
    }
    if (assignedModule === 'vehicle-pursuits') {
      return VehiclePursuitsGenerator.generateTrends(orgId, startDate, endDate);
    }
    if (assignedModule === 'internal-affairs') {
      return InternalAffairsGenerator.generateTrends(orgId, startDate, endDate);
    }

    // Default behavior for other assigned modules
    const actualData: Array<{ period: string; value: number; description: string[]; }> = [];
    const benchmarkData: Array<{ period: string; value: number; description: string[]; }> = [];

    // Generate benchmark value (constant or slightly varying)
    const baseBenchmark = randomInt(10, 30);

    for (let i = 0; i < periods; i++) {
      let periodDate: Date;
      if (useWeeks) {
        periodDate = new Date(start);
        periodDate.setDate(start.getDate() + i * 7);
      } else {
        periodDate = new Date(start.getFullYear(), start.getMonth() + i, 1);
      }

      const period = periodDate.toISOString().split('T')[0];
      const actualValue = randomInt(5, 40);
      const benchmarkValue = baseBenchmark + randomInt(-2, 2);

      const subject = getSubjectIdentifier(assignedModule);
      const subjectPlural = actualValue === 1 ? subject : `${subject}s`;

      actualData.push({
        period,
        value: actualValue,
        description: [`${actualValue} ${subjectPlural}`, '(12-month rolling total)'],
      });

      const benchmarkSubjectPlural = benchmarkValue === 1 ? subject : `${subject}s`;
      benchmarkData.push({
        period,
        value: benchmarkValue,
        description: [`${benchmarkValue} ${benchmarkSubjectPlural}`, '(12-month rolling total)'],
      });
    }

    return {
      periodMode,
      series: [
        {
          name: 'actual',
          data: actualData,
        },
        {
          name: 'benchmark',
          data: benchmarkData,
        },
      ],
    };
  });
}

// Generate dimensions data (must sum to 100%)
export function generateDimensions(
  orgId: string,
  assignedModule: string,
  startDate: string,
  endDate: string
): PerformanceReportingDimensionsResponse {
  const cacheKey = getCacheKey(orgId, assignedModule, startDate, endDate, 'dimensions');

  return getCachedOrGenerate(cacheKey, () => {
    const dimensions: PerformanceReportingDimensionsResponse = [];
    const subject = getSubjectIdentifier(assignedModule);

    switch (assignedModule) {
      case 'internal-affairs': {
        return InternalAffairsGenerator.generateDimensions(orgId, startDate, endDate);
      }
      case 'use-of-force': {
        return UseOfForceGenerator.generateDimensions(orgId, startDate, endDate);
      }
      case 'vehicle-pursuits': {
        return VehiclePursuitsGenerator.generateDimensions(orgId, startDate, endDate);
      }
      // Add more cases for other modules as needed
      default: {
        // Generic dimension for other modules
        const total = randomInt(50, 150);
        const value1 = randomInt(Math.floor(total * 0.3), Math.floor(total * 0.5));
        const value2 = total - value1;

        dimensions.push({
          name: 'Distribution',
          data: [
            {
              name: 'Category A',
              value: value1,
              description: formatTooltipDescription(value1, total, subject),
            },
            {
              name: 'Category B',
              value: value2,
              description: formatTooltipDescription(value2, total, subject),
            },
          ],
        });
      }
    }

    return dimensions;
  });
}

// Generate top-5 summaries (do NOT sum to 100%)
export function generateTop5Summaries(
  orgId: string,
  assignedModule: string,
  startDate: string,
  endDate: string
): PerformanceReportingTop5SummariesResponse {
  const cacheKey = getCacheKey(orgId, assignedModule, startDate, endDate, 'top_5_summaries');

  return getCachedOrGenerate(cacheKey, () => {
    const summaries: PerformanceReportingTop5SummariesResponse = [];
    const subject = getSubjectIdentifier(assignedModule);

    switch (assignedModule) {
      case 'internal-affairs': {
        return InternalAffairsGenerator.generateTop5Summaries(orgId, startDate, endDate);
      }
      case 'use-of-force': {
        return UseOfForceGenerator.generateTop5Summaries(orgId, startDate, endDate);
      }
      case 'vehicle-pursuits': {
        return VehiclePursuitsGenerator.generateTop5Summaries(orgId, startDate, endDate);
      }
      default: {
        // Generic top-5 for other modules
        const total = randomInt(50, 150);
        const values = [
          randomInt(Math.floor(total * 0.3), Math.floor(total * 0.5)),
          randomInt(Math.floor(total * 0.2), Math.floor(total * 0.3)),
          randomInt(Math.floor(total * 0.1), Math.floor(total * 0.2)),
          randomInt(Math.floor(total * 0.05), Math.floor(total * 0.15)),
          randomInt(Math.floor(total * 0.02), Math.floor(total * 0.1)),
        ].sort((a, b) => b - a);

        summaries.push({
          name: 'Top Categories',
          data: values.map((value, index) => ({
            name: `Category ${index + 1}`,
            value,
            description: formatTooltipDescription(value, total, subject),
          })),
        });
      }
    }

    return summaries;
  });
}

